import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  CreatePageTemplateDto,
  CreatePageTemplateTagDto,
  PageTemplateDto,
  PageTemplateDtoListing,
  PageTemplateFormatDto,
  PageTemplateTagDto,
  PersonaZones,
  Role,
  SupportedDevice,
  WorkflowPageConfig,
  cryptoRandomUUID,
} from '@finxone-platform/shared/sys-config-types';
import { Observable, catchError, first, of, switchMap, tap, throwError } from 'rxjs';
import { extractPageTemplatesAndZonesFromPersona } from '../../utils/page-templates.utils';
import { ConfigService } from '../config-service/config.service';

@Injectable({
  providedIn: 'root',
})
export class PageTemplateService {
  private baseUrl = '';
  public currentPageTemplate = signal<PageTemplateDto | null>(null);
  public pageTemplatesToImport: CreatePageTemplateDto[] = [];
  public refreshTemplates = signal(false);
  public isImportPageTemplatesPopupOpen = signal(false);

  private _pageTemplatesFromPersona: CreatePageTemplateDto[];
  public get pageTemplatesFromPersona(): CreatePageTemplateDto[] {
    return this._pageTemplatesFromPersona;
  }

  private _zonesFromPersona: PersonaZones[];
  public get zonesFromPersona(): PersonaZones[] {
    return this._zonesFromPersona;
  }

  private _filteredPagesFromPersona: CreatePageTemplateDto[];
  public get filteredPagesFromPersona(): CreatePageTemplateDto[] {
    return this._filteredPagesFromPersona;
  }
  public filterTemplatePages = signal<PageTemplateDtoListing[]>([]);

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private alertHandlerService: AlertHandlerService,
  ) {
    this.configService.getApi('provisioner_service').subscribe((url) => {
      this.baseUrl = url;
    });
  }

  public syncTemplatesToImport() {
    this.pageTemplatesToImport = this._pageTemplatesFromPersona ? [...this._pageTemplatesFromPersona] : [];
  }

  public toggleTemplateImport(template: CreatePageTemplateDto, checked: boolean) {
    if (checked) {
      if (!this.pageTemplatesToImport.includes(template)) {
        this.pageTemplatesToImport.push(template);
      }
    } else {
      this.pageTemplatesToImport = this.pageTemplatesToImport.filter((t) => t !== template);
    }
  }

  private getRequest<T>(path: string, options?: Record<string, unknown>): Observable<T> {
    return new Observable<T>((observer) => {
      if (!this.baseUrl) {
        this.configService.getApi('provisioner_service').subscribe(
          (url) => {
            this.baseUrl = url;
            this.http.get<T>(`${this.baseUrl}${path}`, options).subscribe(
              (data) => {
                observer.next(data as T);
                observer.complete();
              },
              (error) => observer.error(error),
            );
          },
          (error) => observer.error(error),
        );
      } else {
        this.http.get<T>(`${this.baseUrl}${path}`, options).subscribe(
          (data) => {
            observer.next(data as T);
            observer.complete();
          },
          (error) => observer.error(error),
        );
      }
    });
  }

  private putRequest<T>(path: string, body: unknown): Observable<T> {
    return new Observable<T>((observer) => {
      if (!this.baseUrl) {
        this.configService.getApi('provisioner_service').subscribe(
          (url: string) => {
            this.baseUrl = url;
            this.http.put<T>(`${this.baseUrl}${path}`, body).subscribe(
              (data: T) => {
                observer.next(data);
                observer.complete();
              },
              (error) => observer.error(error),
            );
          },
          (error) => observer.error(error),
        );
      } else {
        this.http.put<T>(`${this.baseUrl}${path}`, body).subscribe(
          (data: T) => {
            observer.next(data);
            observer.complete();
          },
          (error) => observer.error(error),
        );
      }
    });
  }

  private deleteRequest<T, P = void>(path: string, body?: P): Observable<T> {
    const options = body !== undefined ? { body } : {};

    return new Observable<T>((observer) => {
      if (!this.baseUrl) {
        this.configService.getApi('provisioner_service').subscribe(
          (url) => {
            this.baseUrl = url;
            this.http.delete<T>(`${this.baseUrl}${path}`, options).subscribe(
              (data) => {
                observer.next(data);
                observer.complete();
              },
              (error) => observer.error(error),
            );
          },
          (error) => observer.error(error),
        );
      } else {
        this.http.delete<T>(`${this.baseUrl}${path}`, options).subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (error) => observer.error(error),
        );
      }
    });
  }

  private postRequest<T>(path: string, body: any): Observable<T> {
    return new Observable<T>((observer) => {
      if (!this.baseUrl) {
        this.configService.getApi('provisioner_service').subscribe(
          (url) => {
            this.baseUrl = url;
            this.http.post<T>(`${this.baseUrl}${path}`, body).subscribe(
              (data) => {
                observer.next(data);
                observer.complete();
              },
              (error) => observer.error(error),
            );
          },
          (error) => observer.error(error),
        );
      } else {
        this.http.post<T>(`${this.baseUrl}${path}`, body).subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (error) => observer.error(error),
        );
      }
    });
  }

  public setCurrentPageTemplate(template: PageTemplateDto) {
    this.currentPageTemplate.set(template);
  }

  public getPageTemplatesList(query?: {
    searchTerm?: string;
    pageKey?: string;
    zoneKey?: string;
    tag?: string[];
    providerTag?: string;
  }): Observable<PageTemplateDtoListing[]> {
    return this.getRequest<PageTemplateDtoListing[]>('/template/page', { params: query });
  }

  public getPageTemplateWithClosestMatchingTags(query: {
    pageKey: string;
    tag: string[];
  }): Observable<PageTemplateDtoListing[]> {
    return this.getRequest<PageTemplateDtoListing[]>('/template/page/matching-tags', { params: query });
  }

  public createPageTemplate(payload: CreatePageTemplateDto[]): Observable<PageTemplateDto[]> {
    return this.putRequest<PageTemplateDto[]>('/template/page', payload);
  }

  public updatePageTemplate(templateOverride?: PageTemplateDto): Observable<PageTemplateDto> {
    const template = templateOverride || this.currentPageTemplate();
    if (!template) {
      return throwError(() => new Error('No current page template'));
    }
    return this.putRequest<PageTemplateDto>('/template/page', [template]);
  }

  public getPageTemplateById(id: string): Observable<PageTemplateDto> {
    return this.getRequest<PageTemplateDto>(`/template/page/${id}`);
  }

  public deletePageTemplate(id: string, activeDevice: SupportedDevice): Observable<PageTemplateDto> {
    return this.deleteRequest<PageTemplateDto, { activeDevice: SupportedDevice }>(`/template/page/${id}`, {
      activeDevice,
    });
  }

  public importPersonaConfig(persona: Role, tags?: CreatePageTemplateTagDto[]) {
    const { pageTemplates, personaZones } = extractPageTemplatesAndZonesFromPersona({ persona, tags });
    this._pageTemplatesFromPersona = pageTemplates;
    this._zonesFromPersona = personaZones;
    this._filteredPagesFromPersona = pageTemplates;
    this.syncTemplatesToImport(); // Ensure all are checked by default
  }

  public getPageTemplateFormats(): Observable<PageTemplateFormatDto[]> {
    return this.getRequest<PageTemplateFormatDto[]>('/template/format');
  }

  public addPageTemplateFormat(name: string): Observable<PageTemplateFormatDto> {
    return this.putRequest<PageTemplateFormatDto>('/template/format', {
      name,
    });
  }

  public getPageTemplateTags(): Observable<PageTemplateTagDto[]> {
    return this.getRequest<PageTemplateFormatDto[]>('/template/tag');
  }

  public addPageTemplateTag(name: string, description?: string): Observable<CreatePageTemplateTagDto> {
    return this.putRequest<CreatePageTemplateTagDto>('/template/tag', {
      name,
      description,
    });
  }

  public deletePageTemplateTag(id: string): Observable<PageTemplateTagDto> {
    return this.deleteRequest<PageTemplateTagDto>(`/template/tag/${id}`);
  }

  public getProviderTags(): Observable<PageTemplateTagDto[]> {
    return this.getRequest<PageTemplateTagDto[]>('/provider-tag');
  }

  public pastePageTemplate(
    templateId: string,
    device: SupportedDevice,
    pageConfig: WorkflowPageConfig,
    overwriteUrl = false,
  ): Observable<boolean> {
    return this.getPageTemplateById(templateId).pipe(
      first(),
      switchMap((template) => {
        if (!template.devicesPageConfig) {
          return of(false);
        }

        const updatedTemplate = {
          ...template,
          name: overwriteUrl ? pageConfig.name : template.name,
          pageKey: overwriteUrl ? pageConfig.pageConfiguration?.url : template.pageKey,
          devicesPageConfig: {
            ...template.devicesPageConfig,
            [device]: {
              ...pageConfig,
              pageUniqueId: cryptoRandomUUID(),
              name: !overwriteUrl ? template.devicesPageConfig[device]?.name : pageConfig.name,
              title: !overwriteUrl ? template.devicesPageConfig[device]?.title : pageConfig.title,
              pageConfiguration: {
                ...pageConfig.pageConfiguration,
                url: !overwriteUrl
                  ? template.devicesPageConfig[device]?.pageConfiguration?.url
                  : pageConfig.pageConfiguration?.url,
              },
            },
          },
        };

        return this.updatePageTemplate(updatedTemplate).pipe(
          first(),
          switchMap(() => of(true)),
          catchError(() => of(false)),
        );
      }),
      catchError(() => of(false)),
    );
  }

  public pastePageTemplateProperties(
    templateId: string,
    device: SupportedDevice,
    pageConfig: WorkflowPageConfig,
  ): Observable<boolean> {
    return this.getPageTemplateById(templateId).pipe(
      first(),
      switchMap((template) => {
        if (!template.devicesPageConfig) {
          return of(false);
        }

        const updatedTemplate = {
          ...template,
          devicesPageConfig: {
            ...template.devicesPageConfig,
            [device]: {
              ...template.devicesPageConfig[device],
              pageConfiguration: {
                ...pageConfig.pageConfiguration,
                url: template.devicesPageConfig[device]?.pageConfiguration?.url,
              },
            },
          },
        };

        return this.updatePageTemplate(updatedTemplate).pipe(
          first(),
          switchMap(() => of(true)),
          catchError(() => of(false)),
        );
      }),
      catchError(() => of(false)),
    );
  }

  public updateTemplatePageConfig(
    templateId: string,
    device: SupportedDevice,
    pageConfig: WorkflowPageConfig,
  ) {
    return this.getPageTemplateById(templateId).pipe(
      first(),
      tap((template) => {
        if (!template.devicesPageConfig) {
          this.alertHandlerService.showAlertFn('error', 'No devices page config found');
          return;
        }
        //Update template name and pageKey if pageConfiguration.url is changed
        if (pageConfig.title !== template.devicesPageConfig[device].title) {
          template.name = pageConfig.title;
        }
        if (pageConfig.pageConfiguration?.url !== template.devicesPageConfig[device].pageConfiguration?.url) {
          template.pageKey = pageConfig.pageConfiguration?.url;
        }
        template.devicesPageConfig[device] = pageConfig;

        this.updatePageTemplate(template)
          .pipe(first())
          .subscribe(() => {
            this.refreshTemplates.set(true);
            this.alertHandlerService.showAlertFn('success', 'Template updated successfully');
          });
      }),
    );
  }

  public filterPageTemplatesByZones(filterZones: string[]) {
    if (!filterZones?.length) {
      this._filteredPagesFromPersona = [...this._pageTemplatesFromPersona];
      return;
    }
    const zonesSet = new Set(filterZones);
    this._filteredPagesFromPersona = this._pageTemplatesFromPersona.filter((page) =>
      zonesSet.has(page.zoneKey),
    );
  }

  public applyBulkTagsToPageTemplates(
    pages: CreatePageTemplateDto[],
    tagsToApply: CreatePageTemplateTagDto[],
  ) {
    const tagsToApplySet = new Set(tagsToApply);
    pages.forEach((page) => this.applyUniqueTagsToPage(page, tagsToApplySet));
  }

  private applyUniqueTagsToPage(page: CreatePageTemplateDto, tagsToApply: Set<CreatePageTemplateTagDto>) {
    if (tagsToApply.size === 0) return;
    const existingTagIds = new Set(page.tags.map((tag) => tag.id));
    const uniqueTagsToApply = this.getUniqueTagsToApplyOnPageTemplates(existingTagIds, tagsToApply);
    if (uniqueTagsToApply.length > 0) {
      page.tags = [...page.tags, ...uniqueTagsToApply];
    }
  }

  /**
   * Returns a unique tags array to assign on page templates
   * @param existingTagIds
   * @param tagsToApply
   * @returns an array of type CreatePageTemplateTagDto[]
   */
  private getUniqueTagsToApplyOnPageTemplates(
    existingTagIds: Set<string>,
    tagsToApply: Set<CreatePageTemplateTagDto>,
  ): CreatePageTemplateTagDto[] {
    const tagsToAdd: CreatePageTemplateTagDto[] = [];
    const processedIds = new Set(existingTagIds);
    tagsToApply.forEach((tag) => {
      if (!processedIds.has(tag.id)) {
        tagsToAdd.push(tag);
        processedIds.add(tag.id);
      }
    });
    return tagsToAdd;
  }

  public applyTemplateAndProviderTagsToPageTemplates(providerTags: string[], templateId: string) {
    this._pageTemplatesFromPersona.forEach((page) =>
      this.setTemplateIdAndProvidersOnPageTemplate(page, templateId, providerTags),
    );
  }

  private setTemplateIdAndProvidersOnPageTemplate(
    page: CreatePageTemplateDto,
    templateId: string,
    providerTags: string[],
  ) {
    page.formatId = templateId;
    page.providerTags = providerTags;
  }
}
